import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr, API } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
    ArrowsClockwise, UploadSimple, CheckCircle, HandCoins, Warning, FloppyDisk, XCircle,
} from "@phosphor-icons/react";

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ContractPayouts() {
    const { user } = useAuth();
    const canApprove = user?.role === "founder";
    const canPost = user?.role === "founder" || user?.role === "accountant";
    const [month, setMonth] = useState(currentMonth());
    const [runs, setRuns] = useState([]);
    const [summary, setSummary] = useState({});
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [workers, setWorkers] = useState([]);
    const [reviewOpen, setReviewOpen] = useState(null); // preview data
    const [payTarget, setPayTarget] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [r, s, lg, w] = await Promise.all([
                api.get("/contract-payouts/runs", { params: { month } }),
                api.get("/contract-payouts/summary", { params: { month } }),
                api.get("/ledgers"),
                api.get("/contract-workers"),
            ]);
            setRuns(r.data);
            setSummary(s.data);
            setLedgers(lg.data);
            setWorkers(w.data.filter((x) => x.active !== false));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [month]);
    useEffect(() => { refresh(); }, [refresh]);

    const bankCash = useMemo(() => ledgers.filter((l) => ["bank", "cash"].includes(l.party_type)), [ledgers]);

    const approve = async (rid) => {
        try { await api.post(`/contract-payouts/runs/${rid}/approve`); toast.success("Approved. Learned rates updated."); refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const postRun = async (rid) => {
        try { await api.post(`/contract-payouts/runs/${rid}/post`); toast.success("Journal posted."); refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const cancelDraft = async (rid) => {
        if (!confirm("Delete this draft run?")) return;
        try { await api.delete(`/contract-payouts/runs/${rid}`); toast.success("Draft deleted."); refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Contract payouts"
                sub="Upload the monthly piece-rate chart. System detects rate changes vs the last-approved rate. Founder approves → journal auto-posts with TDS."
                right={
                    <div className="flex items-center gap-2">
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="cp-month" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="cp-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => setUploadOpen(true)} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cp-upload">
                            <UploadSimple size={14} className="mr-2" /> Upload chart
                        </Button>
                        {user?.role === "founder" ? (
                            <Button onClick={() => setManualOpen(true)} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="cp-manual">
                                <HandCoins size={14} className="mr-2" /> Manual payout
                            </Button>
                        ) : null}
                    </div>
                } />

            <div className="p-8 space-y-6">
                <div className="grid grid-cols-4 gap-4">
                    <Stat label="Gross payout" value={summary.gross_amount || 0} />
                    <Stat label="TDS withheld" value={summary.tds_amount || 0} tone="amber" />
                    <Stat label="Net to workers" value={summary.net_payable || 0} tone="teal" />
                    <Stat label="Outstanding" value={summary.outstanding || 0} tone="amber" bold />
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        {month} — {runs.length} run{runs.length === 1 ? "" : "s"}
                    </div>
                    <table className="se-table" data-testid="cp-table">
                        <thead>
                            <tr>
                                <th>Worker</th><th>Entity</th><th className="text-right">Items</th>
                                <th className="text-right">Gross</th><th className="text-right">TDS</th>
                                <th className="text-right">Net</th><th className="text-right">Paid / Owed</th>
                                <th>Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 ? (
                                <tr><td colSpan="9" className="py-10 text-center text-stone-500">
                                    No contract payouts for {month}. Upload a chart to start.
                                </td></tr>
                            ) : runs.map((r) => (
                                <tr key={r.id} data-testid={`cp-row-${r.id}`}>
                                    <td><button className="hover:underline text-left" onClick={() => setDetailTarget(r.id)} data-testid={`cp-detail-${r.id}`}>{r.worker_name}</button>
                                        <div className="text-[10px] text-stone-500">{r.worker_code}</div></td>
                                    <td className="text-xs capitalize">{r.entity_type} ({r.tds_rate_pct}%)</td>
                                    <td className="se-num text-right text-xs">{r.entries?.length || 0}</td>
                                    <td className="se-num text-right font-medium">₹ {inr(r.gross_amount)}</td>
                                    <td className="se-num text-right text-xs text-amber-700">₹ {inr(r.tds_amount)}</td>
                                    <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>₹ {inr(r.net_payable)}</td>
                                    <td className="se-num text-right text-xs">
                                        {r.status === "posted" || r.status === "fully_paid" ? (
                                            <>
                                                <div>₹ {inr(r.amount_paid || 0)}</div>
                                                {r.outstanding > 0 ? <div className="text-amber-700">owe ₹ {inr(r.outstanding)}</div> : null}
                                            </>
                                        ) : "—"}
                                    </td>
                                    <td><StatusChip status={r.status} /></td>
                                    <td className="text-right">
                                        {r.status === "draft" ? (
                                            <>
                                                {canApprove ? (
                                                    <Button size="sm" onClick={() => approve(r.id)}
                                                        style={{ background: "#16a34a", color: "#fff" }}
                                                        data-testid={`cp-approve-${r.id}`} className="mr-1">
                                                        <CheckCircle size={13} className="mr-1" /> Approve
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-stone-500 italic mr-2" data-testid={`cp-pending-${r.id}`}>
                                                        Awaiting founder approval
                                                    </span>
                                                )}
                                                <Button size="sm" variant="outline" onClick={() => cancelDraft(r.id)}
                                                    data-testid={`cp-cancel-${r.id}`}>
                                                    <XCircle size={13} />
                                                </Button>
                                            </>
                                        ) : r.status === "approved" ? (
                                            canPost ? (
                                                <Button size="sm" onClick={() => postRun(r.id)}
                                                    style={{ background: "#0d6efd", color: "#fff" }} data-testid={`cp-post-${r.id}`}>
                                                    Post
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-stone-500 italic" data-testid={`cp-postpending-${r.id}`}>
                                                    Awaiting posting
                                                </span>
                                            )
                                        ) : r.status === "posted" && r.outstanding > 0 ? (
                                            canPost ? (
                                                <Button size="sm" onClick={() => setPayTarget(r)}
                                                    style={{ background: "#f59e0b", color: "#fff" }} data-testid={`cp-pay-${r.id}`}
                                                    title="Book payment from Cash or Fiat. For real bank transfers, upload the bank statement instead.">
                                                    <HandCoins size={13} className="mr-1" /> Pay as Cash/Fiat
                                                </Button>
                                            ) : null
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <UploadChartDialog open={uploadOpen} onOpenChange={setUploadOpen} month={month}
                               workers={workers}
                               onPreviewed={(data) => { setUploadOpen(false); setReviewOpen(data); }} />
            <ReviewDialog target={reviewOpen} onClose={() => setReviewOpen(null)} month={month} onSaved={() => { setReviewOpen(null); refresh(); }} />
            <PayDialog target={payTarget} onClose={() => setPayTarget(null)} bankCash={bankCash} onSaved={refresh} />
            <ManualDialog open={manualOpen} onClose={() => setManualOpen(false)} workers={workers} month={month} onSaved={refresh} />
            <DetailDialog runId={detailTarget} onClose={() => setDetailTarget(null)} />
        </>
    );
}

function Stat({ label, value, tone, bold }) {
    const color = tone === "amber" ? "#b45309" : tone === "teal" ? "#0f766e" : "var(--se-ink)";
    return (
        <div className="bg-white border rounded-md p-4" style={{ borderColor: "var(--se-border)" }}>
            <div className="text-xs text-stone-500">{label}</div>
            <div className={`se-num text-2xl ${bold ? "font-semibold" : ""}`} style={{ color }}>₹ {inr(value || 0)}</div>
        </div>
    );
}

function UploadChartDialog({ open, onOpenChange, month, workers, onPreviewed }) {
    const [file, setFile] = useState(null);
    const [workerId, setWorkerId] = useState("");
    const [settlementMonth, setSettlementMonth] = useState(month);
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setFile(null); setWorkerId(""); setSettlementMonth(month); } }, [open, month]);

    const upload = async () => {
        if (!workerId) return toast.error("Pick the contractor whose payout this is.");
        if (!file) return toast.error("Pick an Excel file first.");
        if (!settlementMonth || !/^\d{4}-\d{2}$/.test(settlementMonth)) return toast.error("Pick a valid settlement month.");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/contract-payouts/preview-chart?month=${settlementMonth}&worker_id=${encodeURIComponent(workerId)}`,
                fd, { headers: { "Content-Type": "multipart/form-data" } });
            const skipped = (data.out_of_month_rows || []).length;
            if (skipped > 0) {
                toast.success(`Parsed ${data.workers.length} worker(s) · ${skipped} row(s) from outside ${settlementMonth} filtered out.`);
            } else {
                toast.success(`Parsed ${data.workers.length} worker(s).`);
            }
            onPreviewed(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="cp-upload-dialog">
                <DialogHeader><DialogTitle>Upload piece-rate chart — {settlementMonth}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Contractor *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm"
                                value={workerId} onChange={(e) => setWorkerId(e.target.value)}
                                data-testid="cp-upload-worker">
                            <option value="">-- Select contractor --</option>
                            {(workers || []).map((w) => (
                                <option key={w.id} value={w.id}>{w.name} ({w.worker_code}) · {w.entity_type}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label>Settlement month *</Label>
                        <Input type="month" value={settlementMonth} onChange={(e) => setSettlementMonth(e.target.value)}
                               data-testid="cp-upload-month" />
                        <div className="text-[11px] text-stone-500 mt-1">
                            Only rows whose <b>Date</b> falls inside this month are processed. Rows dated in any other month are skipped and reported on the next screen. Rows without a date are always accepted.
                        </div>
                    </div>
                    <div className="text-xs text-stone-600 border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#fefce8" }}>
                        <div className="font-semibold mb-1">Excel format for this contractor:</div>
                        <div className="font-mono text-[11px]">Date | Product | Quantity | Rate | Total</div>
                        <div className="mt-2">
                            No worker code needed — every row is billed to the contractor picked above.
                            <br />
                            <b>Rate</b> is optional if you fill <b>Total</b> (rate is derived as Total ÷ Quantity). If Rate is present and differs from the last approved rate, you&apos;ll be asked to confirm the change on the next screen.
                            <br />
                            <b>Date</b> accepts <span className="font-mono">1-June-2026</span>, <span className="font-mono">2026-06-01</span>, or <span className="font-mono">01/06/2026</span>. Column order can be shuffled — the parser matches on the header names.
                        </div>
                    </div>
                    <div><Label>Excel file (.xlsx)</Label>
                        <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cp-upload-file" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={upload} disabled={busy || !file || !workerId} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cp-upload-submit">
                        {busy ? "Parsing..." : "Preview"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewDialog({ target, onClose, month, onSaved }) {
    const [workers, setWorkers] = useState([]);
    const [selected, setSelected] = useState({}); // worker_id -> true
    const [saving, setSaving] = useState(false);
    const [confirmed, setConfirmed] = useState({}); // `${wid}:${i}` -> true
    // Use the settlement month the user actually picked in the upload popup,
    // as echoed back by the preview response. Falls back to the page-level month.
    const settleMonth = target?.month || month;

    useEffect(() => {
        if (target) {
            setWorkers(target.workers);
            const sel = {};
            target.workers.forEach((w) => { sel[w.worker_id] = !w.has_missing_rate; });
            setSelected(sel);
            setConfirmed({});
        }
    }, [target]);

    if (!target) return null;

    const toggle = (wid) => setSelected((s) => ({ ...s, [wid]: !s[wid] }));
    const confirmEntry = (wid, idx) => setConfirmed((c) => ({ ...c, [`${wid}:${idx}`]: !c[`${wid}:${idx}`] }));

    const save = async () => {
        const workersToSave = workers.filter((w) => selected[w.worker_id]);
        // Validate: each deviation must be confirmed
        for (const w of workersToSave) {
            if (w.has_missing_rate) {
                return toast.error(`${w.worker_name} has entries with no rate. Fix the Excel and re-upload.`);
            }
            for (let i = 0; i < w.entries.length; i++) {
                const e = w.entries[i];
                if (e.deviation && !confirmed[`${w.worker_id}:${i}`]) {
                    return toast.error(`Confirm the rate change for ${w.worker_name} → ${e.product}`);
                }
            }
        }
        setSaving(true);
        try {
            for (const w of workersToSave) {
                await api.post("/contract-payouts/runs", {
                    worker_id: w.worker_id, month: settleMonth,
                    entries: w.entries.map((e, i) => ({
                        product: e.product, qty: e.qty, rate: e.effective_rate,
                        row_rate: e.row_rate, learned_rate: e.learned_rate,
                        deviation_confirmed: !!confirmed[`${w.worker_id}:${i}`],
                    })),
                });
            }
            toast.success(`Saved ${workersToSave.length} draft run(s).`);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="cp-review-dialog">
                <DialogHeader><DialogTitle>Review chart — {settleMonth}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    {target.unknown_rows?.length > 0 ? (
                        <div className="text-xs text-red-800 border rounded-md p-3" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
                            <Warning size={14} className="inline mr-1" />
                            {target.unknown_rows.length} row(s) had unknown worker code / name and were skipped. Add the worker(s) first and re-upload if needed.
                        </div>
                    ) : null}
                    {target.out_of_month_rows?.length > 0 ? (
                        <div className="text-xs text-amber-900 border rounded-md p-3" style={{ borderColor: "#fde68a", background: "#fffbeb" }} data-testid="cp-review-oom">
                            <Warning size={14} className="inline mr-1" />
                            <b>{target.out_of_month_rows.length} row(s)</b> in the sheet were dated outside <b>{settleMonth}</b> and skipped — settle them in their own month.
                            <details className="mt-1">
                                <summary className="cursor-pointer text-amber-800">Show skipped rows</summary>
                                <ul className="pl-4 mt-1 space-y-0.5">
                                    {target.out_of_month_rows.slice(0, 20).map((r, i) => (
                                        <li key={i}>
                                            {r.row_date || r.row_month || "?"} · {r.product} · qty {r.qty}{r.rate ? ` · rate ₹${inr(r.rate)}` : ""}
                                        </li>
                                    ))}
                                    {target.out_of_month_rows.length > 20 ? (
                                        <li className="text-amber-800">…and {target.out_of_month_rows.length - 20} more</li>
                                    ) : null}
                                </ul>
                            </details>
                        </div>
                    ) : null}
                    {workers.map((w) => (
                        <div key={w.worker_id} className="border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                            <div className="px-4 py-2 flex items-center justify-between border-b" style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" checked={!!selected[w.worker_id]} onChange={() => toggle(w.worker_id)}
                                           disabled={w.has_missing_rate} data-testid={`cp-review-sel-${w.worker_id}`} />
                                    <div>
                                        <div className="font-medium">{w.worker_name} <span className="text-[11px] text-stone-500">({w.worker_code})</span></div>
                                        <div className="text-[11px] text-stone-500 capitalize">{w.entity_type} · TDS @ {w.tds_rate_pct}%</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-stone-500">Gross → TDS → Net</div>
                                    <div className="se-num text-sm">
                                        ₹ {inr(w.gross_amount)}
                                        <span className="text-amber-700"> − ₹{inr(w.tds_amount)} </span>
                                        <span style={{ color: "#0f766e" }}>= ₹{inr(w.net_payable)}</span>
                                    </div>
                                </div>
                            </div>
                            <table className="w-full text-xs">
                                <thead><tr style={{ borderBottom: "1px solid var(--se-border)" }}>
                                    <th className="px-3 py-2 text-left">Product</th>
                                    <th className="px-3 py-2 text-right">Qty</th>
                                    <th className="px-3 py-2 text-right">Rate</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2">Flag</th>
                                </tr></thead>
                                <tbody>{w.entries.map((e, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid var(--se-border)" }}>
                                        <td className="px-3 py-2">{e.product}</td>
                                        <td className="px-3 py-2 text-right se-num">{e.qty}</td>
                                        <td className="px-3 py-2 text-right se-num">
                                            ₹ {inr(e.effective_rate || 0)}
                                            {e.deviation?.kind === "changed" ? (
                                                <div className="text-[10px] text-amber-700">was ₹ {inr(e.deviation.old_rate)}</div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 text-right se-num font-medium">₹ {inr(e.amount)}</td>
                                        <td className="px-3 py-2">
                                            {e.deviation?.kind === "changed" ? (
                                                <label className="inline-flex items-center gap-1 text-amber-800 cursor-pointer">
                                                    <input type="checkbox" checked={!!confirmed[`${w.worker_id}:${i}`]}
                                                           onChange={() => confirmEntry(w.worker_id, i)}
                                                           data-testid={`cp-confirm-${w.worker_id}-${i}`} />
                                                    Confirm ₹{inr(e.deviation.old_rate)} → ₹{inr(e.deviation.new_rate)}
                                                </label>
                                            ) : e.deviation?.kind === "new_rate" ? (
                                                <label className="inline-flex items-center gap-1 text-emerald-800 cursor-pointer">
                                                    <input type="checkbox" checked={!!confirmed[`${w.worker_id}:${i}`]}
                                                           onChange={() => confirmEntry(w.worker_id, i)}
                                                           data-testid={`cp-confirm-${w.worker_id}-${i}`} />
                                                    First-time rate ₹{inr(e.deviation.new_rate)}
                                                </label>
                                            ) : e.deviation?.kind === "missing_rate" ? (
                                                <span className="text-red-700">
                                                    <Warning size={13} className="inline mr-1" /> No rate — fix Excel
                                                </span>
                                            ) : (
                                                <span className="text-stone-500">Using learned ₹{inr(e.learned_rate || 0)}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cp-review-save">
                        <FloppyDisk size={13} className="mr-1" /> {saving ? "Saving..." : "Save as drafts"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PayDialog({ target, onClose, bankCash, onSaved }) {
    const [ledgerId, setLedgerId] = useState("");
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);
    // Cash/Fiat only. Real bank payments must flow through the Bank Statement
    // upload → auto-map path so ledger + statement stay in sync.
    const cashFiatOnly = useMemo(() => bankCash.filter((l) => l.system === true), [bankCash]);

    useEffect(() => {
        if (target) {
            setAmount(String(target.outstanding || target.net_payable));
            setLedgerId(cashFiatOnly.find((l) => l.name === "FIAT BANK")?.id || (cashFiatOnly[0]?.id || ""));
            setDate(new Date().toISOString().slice(0, 10));
        }
    }, [target, cashFiatOnly]);

    if (!target) return null;

    const submit = async () => {
        if (!ledgerId) return toast.error("Pick a source Cash/Fiat ledger");
        const amt = Number(amount);
        if (!amt || amt <= 0) return toast.error("Enter a valid amount");
        setSaving(true);
        try {
            await api.post(`/contract-payouts/runs/${target.id}/pay`, {
                source_ledger_id: ledgerId, date, amount: amt,
            });
            toast.success("Payment recorded.");
            onSaved && onSaved(); onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="cp-pay-dialog">
                <DialogHeader><DialogTitle>Pay {target.worker_name} — Cash / Fiat ({target.month})</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-[11px] rounded-sm px-3 py-2" style={{ background: "#fef3c7", color: "#78350f" }}>
                        Use this only for cash-in-hand or Fiat drawer payouts. For ICICI / SBI transfers, skip this —
                        upload the bank statement and it will auto-map.
                    </div>
                    <div className="text-xs text-stone-500">Outstanding: <span className="font-medium">₹ {inr(target.outstanding || 0)}</span></div>
                    <div><Label>From ledger *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={ledgerId}
                                onChange={(e) => setLedgerId(e.target.value)} data-testid="cp-pay-ledger">
                            {cashFiatOnly.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Date</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cp-pay-date" /></div>
                        <div><Label>Amount (₹)</Label>
                            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cp-pay-amount" /></div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cp-pay-submit">
                        {saving ? "Recording..." : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DetailDialog({ runId, onClose }) {
    const [run, setRun] = useState(null);
    useEffect(() => {
        if (!runId) return;
        (async () => {
            try { const { data } = await api.get(`/contract-payouts/runs/${runId}`); setRun(data); }
            catch (e) { toast.error(formatApiError(e)); }
        })();
    }, [runId]);

    return (
        <Dialog open={!!runId} onOpenChange={(o) => { if (!o) { setRun(null); onClose(); } }}>
            <DialogContent className="max-w-3xl" data-testid="cp-detail-dialog">
                <DialogHeader><DialogTitle>{run?.worker_name} — {run?.month}</DialogTitle></DialogHeader>
                {run ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            <div><div className="text-xs text-stone-500">Gross</div><div className="se-num font-medium">₹ {inr(run.gross_amount)}</div></div>
                            <div><div className="text-xs text-stone-500">TDS ({run.tds_rate_pct}%)</div><div className="se-num text-amber-700">₹ {inr(run.tds_amount)}</div></div>
                            <div><div className="text-xs text-stone-500">Net</div><div className="se-num font-medium" style={{ color: "#0f766e" }}>₹ {inr(run.net_payable)}</div></div>
                        </div>
                        <table className="se-table w-full">
                            <thead><tr><th>Product</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
                            <tbody>{run.entries?.map((e, i) => (
                                <tr key={i}>
                                    <td>{e.product}</td>
                                    <td className="se-num text-right">{e.qty}</td>
                                    <td className="se-num text-right">₹ {inr(e.rate)}</td>
                                    <td className="se-num text-right">₹ {inr(e.amount)}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                        {run.payments?.length > 0 ? (
                            <div>
                                <div className="text-xs font-semibold text-stone-600 mb-2">Payments</div>
                                <table className="se-table w-full">
                                    <thead><tr><th>Date</th><th>From</th><th className="text-right">Amount</th></tr></thead>
                                    <tbody>{run.payments.map((p, i) => (
                                        <tr key={i}>
                                            <td>{p.date}</td>
                                            <td className="text-xs">{p.source_ledger_name}</td>
                                            <td className="se-num text-right">₹ {inr(p.amount)}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                ) : <div className="text-sm text-stone-500">Loading...</div>}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ManualDialog({ open, onClose, workers, month, onSaved }) {
    const [workerId, setWorkerId] = useState("");
    const [runMonth, setRunMonth] = useState(month || "");
    const [gross, setGross] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setWorkerId(""); setRunMonth(month || ""); setGross(""); setNotes("");
        }
    }, [open, month]);

    const worker = workers.find((w) => w.id === workerId);
    const rate = worker
        ? (worker.entity_type === "company" ? 2 : 1)
        : null;
    const grossNum = Number(gross) || 0;
    const tdsAmt = rate ? Math.round(grossNum * rate) / 100 : 0;
    const netAmt = Math.max(0, grossNum - tdsAmt);

    const submit = async () => {
        if (!workerId) return toast.error("Pick a contractor");
        if (grossNum <= 0) return toast.error("Gross amount must be > 0");
        setSaving(true);
        try {
            await api.post("/contract-payouts/runs/manual", {
                worker_id: workerId, month: runMonth,
                gross_amount: grossNum, notes: notes || null,
            });
            toast.success(`Draft created — ₹${grossNum.toFixed(2)} (TDS ₹${tdsAmt.toFixed(2)})`);
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="cp-manual-dialog">
                <DialogHeader>
                    <DialogTitle>Manual contract payout</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-xs text-stone-600 border rounded-sm p-3"
                         style={{ borderColor: "#bfdbfe", background: "#eff6ff", color: "#1e3a8a" }}>
                        Founder shortcut — enter a lump-sum payout for a contractor without itemising by product. TDS is auto-computed on the gross. The draft then follows the normal approve → post → pay flow.
                    </div>
                    <div>
                        <Label>Contractor</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={workerId}
                                onChange={(e) => setWorkerId(e.target.value)}
                                data-testid="cp-manual-worker">
                            <option value="">-- Select --</option>
                            {workers.map((w) => (
                                <option key={w.id} value={w.id}>{w.name} ({w.worker_code}) · {w.entity_type}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Month</Label>
                            <Input type="month" value={runMonth} onChange={(e) => setRunMonth(e.target.value)} data-testid="cp-manual-month" /></div>
                        <div><Label>Gross amount (₹)</Label>
                            <Input type="number" value={gross} onChange={(e) => setGross(e.target.value)}
                                   placeholder="e.g. 50000" data-testid="cp-manual-gross" /></div>
                    </div>
                    <div><Label>Notes <span className="text-stone-400 text-[11px]">(optional)</span></Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                               placeholder="what this payout is for" data-testid="cp-manual-notes" /></div>
                    {worker && grossNum > 0 ? (
                        <div className="border rounded-sm p-3 text-xs space-y-1"
                             style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}
                             data-testid="cp-manual-preview">
                            <div>Entity type: <b className="capitalize">{worker.entity_type}</b> → TDS @ {rate}%</div>
                            <div>Gross: ₹{inr(grossNum)}</div>
                            <div>TDS: −₹{inr(tdsAmt)}</div>
                            <div className="font-medium text-emerald-700">Net payable: ₹{inr(netAmt)}</div>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !workerId || grossNum <= 0}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="cp-manual-submit">
                        {saving ? "…" : "Create draft"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
